import React, {useState} from 'react';
import dateFormat from 'dateformat';

import getDaysListForMonth from '@stencil/date-utils/src/getDaysListForMonth';
import modifyDateForDate from '@stencil/date-utils/src/modifyDateForDate';
import getMonthDifferenceBetweenTwoDates from '@stencil/date-utils/src/getMonthDifferenceBetweenTwoDates';

import ArrowLeftIcon from './ArrowLeftIcon';
import ArrowRightIcon from './ArrowRightIcon';
import * as S from './dcalendar.styles';

interface IMonthsViewProps {
  renderDateItem: Function;
  dateConfigToBeDisplayed: Date;
}

export const MonthView = (props: IMonthsViewProps) => {
  const {renderDateItem, dateConfigToBeDisplayed} = props;

  const dateConfigForMonth = new Date(dateConfigToBeDisplayed);
  dateConfigForMonth.setDate(1);

  const daysList = getDaysListForMonth(dateConfigForMonth);

  return (
    <>
      {daysList.map(item =>
        renderDateItem({
          value: item,
          dateObj: (item && modifyDateForDate(dateConfigForMonth, item)) || null,
        }),
      )}
    </>
  );
};

function useCalendarView({
  dateConfigForMonthToBeDisplayedFromProps,
  minDateAllowed,
  maxDateAllowed,
}) {
  const [dateConfigForMonthToBeDisplayed, setDateConfigForMonthToBeDisplayed] = useState(
    dateConfigForMonthToBeDisplayedFromProps,
  );

  const hideLeftArrow =
    getMonthDifferenceBetweenTwoDates(minDateAllowed, dateConfigForMonthToBeDisplayed) === 0;

  const hideRightArrow =
    getMonthDifferenceBetweenTwoDates(dateConfigForMonthToBeDisplayed, maxDateAllowed) === 0;

  const leftArrowClickHandler = () => {
    if (hideLeftArrow) return;
    dateConfigForMonthToBeDisplayed.setMonth(dateConfigForMonthToBeDisplayed.getMonth() - 1);
    setDateConfigForMonthToBeDisplayed(new Date(dateConfigForMonthToBeDisplayed));
  };

  const rightArrowClickHandler = () => {
    if (hideRightArrow) return;
    dateConfigForMonthToBeDisplayed.setMonth(dateConfigForMonthToBeDisplayed.getMonth() + 1);
    setDateConfigForMonthToBeDisplayed(new Date(dateConfigForMonthToBeDisplayed));
  };

  return [
    hideLeftArrow,
    hideRightArrow,
    leftArrowClickHandler,
    rightArrowClickHandler,
    dateConfigForMonthToBeDisplayed,
  ];
}

interface ICalendarViewProps {
  renderDateItem: Function;
  dateConfigForMonthToBeDisplayed: Date;
  minDateAllowed: Date;
  maxDateAllowed: Date;
  extendedComponent?: Function;
}

export const CalendarView = (props: ICalendarViewProps) => {
  const {
    renderDateItem,
    dateConfigForMonthToBeDisplayed: dateConfigForMonthToBeDisplayedFromProps,
    minDateAllowed,
    maxDateAllowed,
    extendedComponent,
  } = props;

  const tempDateObjToBeUsedForMonthToBeDisplayedArg = new Date(
    dateConfigForMonthToBeDisplayedFromProps,
  );
  tempDateObjToBeUsedForMonthToBeDisplayedArg.setDate(1);

  const [
    hideLeftArrow,
    hideRightArrow,
    leftArrowClickHandler,
    rightArrowClickHandler,
    dateConfigForMonthToBeDisplayed,
  ] = useCalendarView({
    dateConfigForMonthToBeDisplayedFromProps: tempDateObjToBeUsedForMonthToBeDisplayedArg,
    minDateAllowed,
    maxDateAllowed,
  });

  return (
    <>
      <S.CalendarWrapDiv>
        <S.CalendarMainComponentWrapper>
          <S.OuterMonthWrapDiv>
            <S.InnerMonthWrapDiv>
              <S.MonthChangeLeftArrowDiv
                onClick={() => {
                  leftArrowClickHandler();
                }}
                data-testid="calendarLeftArrowBtn"
                disabled={hideLeftArrow}
              >
                <S.SliderArrow>
                  <ArrowLeftIcon fill={(hideLeftArrow && '#accff9') || ''} />
                </S.SliderArrow>
              </S.MonthChangeLeftArrowDiv>

              <S.MonthNamePara>
                {dateFormat(dateConfigForMonthToBeDisplayed, 'mmmm yyyy')}
              </S.MonthNamePara>
              <S.MonthChangeRightArrowDiv
                onClick={() => {
                  rightArrowClickHandler();
                }}
                data-testid="calendarRightArrowBtn"
                disabled={hideRightArrow}
              >
                <S.SliderArrow>
                  <ArrowRightIcon fill={(hideRightArrow && '#accff9') || ''} />
                </S.SliderArrow>
              </S.MonthChangeRightArrowDiv>
            </S.InnerMonthWrapDiv>
          </S.OuterMonthWrapDiv>
          <S.CalendarMainWrapperDiv>
            <S.MonthWrapper>
              <S.DayAndDateWrapperDiv>
                <S.DayWrapDiv>
                  {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map(item => (
                    <li>{item}</li>
                  ))}
                </S.DayWrapDiv>
                <S.DateWrapDiv>
                  <MonthView
                    renderDateItem={renderDateItem}
                    dateConfigToBeDisplayed={dateConfigForMonthToBeDisplayed}
                  />
                </S.DateWrapDiv>
              </S.DayAndDateWrapperDiv>
            </S.MonthWrapper>
          </S.CalendarMainWrapperDiv>
        </S.CalendarMainComponentWrapper>
      </S.CalendarWrapDiv>
      {extendedComponent && extendedComponent(dateConfigForMonthToBeDisplayed)}
    </>
  );
};
